import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import { TitlelessCard, Icon, IconDefinitions } from "../../../../components";
import { AreaExport, InspectionService } from "../../../../services";
import { InspectionSessionDetailsModel } from "../../../../data/entities";
import { FileExportType } from "../../../../models/FileExport";

interface InspectionReportCardProps {
  inspection: InspectionSessionDetailsModel;
  inspectionId: string;
  onApprovalRequested: () => void;
}

export function InspectionReportCard({ inspection, inspectionId, onApprovalRequested }: InspectionReportCardProps) {
  const navigate = useNavigate();
  const service = new InspectionService();

  const [downloading, setDownloading] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const handleRequestApproval = async () => {
    setRequesting(true);
    try {
      await service.requestApproval(parseInt(inspectionId));
      onApprovalRequested();
    } catch (error) {
      console.error("Error requesting approval:", error);
    } finally {
      setRequesting(false);
    }
  };

  const handleDownloadReport = () => {
    const pdfFileName = `${inspection.reference ?? inspectionId}-${new Date(inspection.inspectionDate ?? inspection.createdDate ?? "").toISOString().slice(0, 10)}`;
    const exportUrls = service.getExportUrls(
      parseInt(inspectionId),
      AreaExport.inspection,
      pdfFileName
    );
    const pdfExport = exportUrls.find((f) => f.type === FileExportType.Report);

    if (!pdfExport) return;

    setDownloading(true);
    service
      .exportFile(pdfExport.url)
      .then((file) => {
        if (!file) return;
        const blob = new Blob([file.data], { type: pdfExport.contentType });
        const objUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download =
          file.name ?? `${pdfFileName}.${pdfExport.fileExtension}`;
        link.target = "_blank";
        link.href = objUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objUrl);
      })
      .finally(() => {
        setDownloading(false);
      });
  };

  return (
    <TitlelessCard className="p-3">
     <div className="section-label">
        Inspection Report
      </div>

      <div className="d-flex align-items-center gap-3 px-3 py-2 mb-3 border rounded bg-light">
        <Icon
          iconName={IconDefinitions.fileAlt}
          className="text-muted fs-5"
        />
        <div className="flex-fill">
          <div className="fw-semibold">
            {inspection.isPublished ? "Report ready" : "Report pending"}
          </div>
          {inspection.publishedDate && (
            <div className="text-muted" style={{ fontSize: "0.75rem" }} >
                Generated {new Date(inspection.publishedDate).toLocaleDateString()}
            </div>
          )}
        </div>
        <span
          className={`rounded-circle flex-shrink-0 ${inspection.isPublished ? "bg-success" : "bg-warning"}`}
          style={{ width: 8, height: 8}}
        />
      </div>

      <div className="d-flex gap-2">
        {inspection.isPublished ? (
          <>
            <Button
              variant="primary"
              size="sm"
              className="flex-fill"
              onClick={() =>
                navigate(`/home/myinspections/${inspectionId}/details`)
              }
            >
              <Icon iconName={IconDefinitions.file} className="me-2" />
              View Inspection
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="flex-fill"
              disabled={downloading}
              onClick={handleDownloadReport}
            >
              {downloading ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <Icon iconName={IconDefinitions.download} className="me-2" />
              )}
              Download Report
            </Button>
          </>
        ) : inspection.approvalRequested ? (
          <Button
            variant="secondary"
            size="sm"
            className="flex-fill"
            disabled
          >
            <Icon iconName={IconDefinitions.clock} className="me-2" />
            Pending Report
          </Button>
        ) : (
          <Button
            variant="primary"
            size="sm"
            className="flex-fill"
            disabled={requesting}
            onClick={handleRequestApproval}
          >
            {requesting ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : (
              <Icon iconName={IconDefinitions.file} className="me-2" />
            )}
            Request Report
          </Button>
        )}
      </div>
    </TitlelessCard>
  );
}